import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { FinancingCollectionModel, SubventionModel } from './subvention.model';
import { SubventionTypeModel } from './subventionType.model';
import { environment } from '../../../../environments/environment';
import { SubventionAmortisationModel } from './amortisation/subvention-amortisation.model';
import { ResultModel } from '../../../shared/result.model';
import { ResultTotalModel } from '../../../shared/total.model';
import { ApiResponse } from '../../../models/base';

const LIST_ENDPOINT = '/subventions';
const LIST_TYPES_ENDPOINT = '/subventionTypes';

interface ApiMessageResponse {
  message: string;
}

interface ApiMessageUppercaseResponse {
  Message: string;
}

@Injectable({
  providedIn: 'root',
})
export class SubventionService {
  subventionList: SubventionModel[] = [];

  private readonly http = inject(HttpClient);

  async pdfMail(uid: string): Promise<string> {
    const response = await firstValueFrom(
      this.http.get<ApiMessageResponse>(environment.apiUrl + `/pdfmail/${uid}`)
    );

    return response.message;
  }

  async pdf(uid: string): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.get(environment.apiUrl + `/pdf/${uid}`, {
        responseType: 'blob',
      })
    );

    return new Blob([response], { type: 'application/pdf' });
  }

  async addData(preinvoice: SubventionModel): Promise<SubventionModel> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<SubventionModel>>(
        environment.apiUrl + LIST_ENDPOINT,
        preinvoice
      )
    );

    return response.data;
  }

  async deleteData(uid: string): Promise<string> {
    const response = await firstValueFrom(
      this.http.delete<ApiMessageUppercaseResponse>(
        environment.apiUrl + `${LIST_ENDPOINT}/${uid}`
      )
    );

    return response.Message;
  }

  // Financiación (asociaciones): cobros totales o parciales (asiento 572 / 4708.1 por cada uno).
  async collectFinancing(uid: string, paided: string, bankAccountUid: string, amount: number): Promise<void> {
    await firstValueFrom(
      this.http.post(environment.apiUrl + `${LIST_ENDPOINT}/${uid}/collect`, { paided, bankAccountUid, amount })
    );
  }

  async getFinancingCollections(uid: string): Promise<FinancingCollectionModel[]> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<FinancingCollectionModel[]>>(environment.apiUrl + `${LIST_ENDPOINT}/${uid}/collections`)
    );
    return response.data || [];
  }

  // Deshace un cobro concreto: borra solo su asiento (la concesión y los demás cobros se mantienen).
  async uncollectFinancing(uid: string, collectionUid: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(environment.apiUrl + `${LIST_ENDPOINT}/${uid}/collections/${collectionUid}`)
    );
  }

  async editData(uid: string, preinvoice: SubventionModel): Promise<SubventionModel> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<SubventionModel>>(
        environment.apiUrl + `${LIST_ENDPOINT}/${uid}/edit`,
        preinvoice
      )
    );

    return response.data;
  }

  async loadData(uid: string): Promise<SubventionModel> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<SubventionModel>>(
        environment.apiUrl + `${LIST_ENDPOINT}/${uid}/get`
      )
    );

    return response.data;
  }

  async getList(offset?: number, limit?: number): Promise<ResultModel<SubventionModel>> {
    let params = new HttpParams();

    if (offset !== undefined) {
      params = params.set('offset', offset.toString());
    }

    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<ResultModel<SubventionModel>>>(
        environment.apiUrl + LIST_ENDPOINT,
        {
          params,
        }
      )
    );

    return response.data;
  }

  async getTotal(): Promise<ResultTotalModel[]> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<ResultTotalModel[]>>(
        environment.apiUrl + LIST_ENDPOINT + '/total'
      )
    );

    return response.data;
  }

  getListTypes(): Observable<SubventionTypeModel[]> {
    return this.http
      .get<ApiResponse<SubventionTypeModel[]>>(environment.apiUrl + LIST_TYPES_ENDPOINT)
      .pipe(map((response) => response.data));
  }

  async export(uid: string): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.get(environment.apiUrl + `${LIST_ENDPOINT}/amortisation/excel/${uid}`, {
        responseType: 'blob',
      })
    );

    return new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  async getAmortisation(uid: string): Promise<SubventionAmortisationModel[]> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<SubventionAmortisationModel[]>>(
        environment.apiUrl + `${LIST_ENDPOINT}/amortisation/${uid}`
      )
    );

    return response.data;
  }

  async getBookAmortisation(uids: string[], clientUid: string): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.post(environment.apiUrl + LIST_ENDPOINT + '/bookAmortisation', uids, {
        responseType: 'blob',
      })
    );

    return new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }
}